import React, { useState, useRef } from 'react';
import { Image as ImageIcon, Video, ExternalLink,  Award  } from 'lucide-react';
import { galleryImages, galleryVideos } from '../mockData';
import ImageLightbox from '../components/ImageLightbox';
import AwardsPage from './AwardsPage';
import PressPage from './PressPage';
import CsrActivity from './CsrActivity';
import Footer from '../components/Footer';

const GalleryPage = () => {
  const [activeGalleryTab, setActiveGalleryTab] = useState('press');
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeVideo, setActiveVideo] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(-1);

  React.useEffect(() => {
    if (activeVideo !== null || selectedImageIndex !== -1) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [activeVideo, selectedImageIndex]);

  const allGalleryItems = [
    ...galleryImages.map(item => ({ ...item, category: 'image' })),
    ...galleryVideos.map(item => ({ ...item, category: 'video' }))
  ];
  const videoRefs = useRef([]);
  const iframeRefs = useRef([]);

  const pauseAll = (currentIndex, type) => {
    videoRefs.current.forEach((vid, i) => {
      if (vid && i !== currentIndex) vid.pause();
    });
    iframeRefs.current.forEach((frame, i) => {
      if (frame && i !== currentIndex) {
        frame.contentWindow.postMessage(
          JSON.stringify({ event: "command", func: "pauseVideo" }),
          "*"
        );
      }
    });
  };


  const filteredItems = allGalleryItems.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase());

    if (activeGalleryTab === 'gallery') return matchesSearch;
    if (activeGalleryTab === 'image') return item.category === 'image' && matchesSearch;
    if (activeGalleryTab === 'video') return item.category === 'video' && matchesSearch;

    return false;
  });

  const galleryTabs = [
    // { id: 'gallery', name: 'Gallery', icon: Grid },
    { id: 'press', name: 'Awards', icon: Award },
    { id: 'image', name: 'Images', icon: ImageIcon },
    // { id: 'video', name: 'Videos', icon: Video },
    // { id: 'handover', name: 'Key Handover', icon: Key },
    // { id: 'credai', name: 'CREDAI Expo', icon: Video }
  ];

  return (
    <div className="min-h-screen text-white">
      {/* Hero Section */}
      <section
        className="relative flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: "url('/project/gallery-hero.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="absolute inset-0 bg-black/70"></div>

        <div className="relative z-10 max-w-7xl px-4 sm:px-8 md:px-16 lg:px-20 text-center py-24 md:py-32 lg:py-40">
           
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold leading-tight mb-4 sm:mb-6">
            <span className="text-white">Media</span> <span className="text-white">Gallery</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl max-w-3xl sm:max-w-4xl mx-auto leading-relaxed ">
            Explore our achievements, press coverage, and social impact through our
            comprehensive media collection.
          </p>
        </div>
      </section>
      {/* Main Gallery Tabs */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-20 z-40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-2">
            {galleryTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveGalleryTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${activeGalleryTab === tab.id
                    ? 'bg-[#011321] text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-[#011321]'
                    }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery Content */}
      {['gallery', 'image', 'video'].includes(activeGalleryTab) && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredItems.map((item, idx) => (
                  <div key={item.id} className="group relative bg-gray-900 rounded-2xl overflow-hidden shadow-lg hover:shadow-[#011321]/40 transition-all duration-500 transform hover:-translate-y-2">
                    <div className="relative aspect-square overflow-hidden">

                      
                      {item.category === 'image' && (
                        <img
                          src={item.url}
                          alt={item.title}
                          className="w-full h-full object-fill group-hover:scale-110 transition-transform duration-700"
                          loading="lazy"
                        />
                      )}

                      {item.category === "video" && (
                        <>
                          {item.url.includes("youtube") ? (
                            <iframe
                              className="rounded-2xl w-full h-full"
                              src={item.url + "?enablejsapi=1"}
                              title={item.title}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                              ref={(el) => (iframeRefs.current[idx] = el)}
                              onLoad={() => pauseAll(idx, "iframe")}
                            />
                          ) : (
                            <video
                              className="w-full h-full object-cover"
                              src={item.url}
                              controls
                              preload="metadata"
                              ref={(el) => (videoRefs.current[idx] = el)}
                              onPlay={() => pauseAll(idx, "video")}
                            />
                          )}
                        </>
                      )}




                     
                      {/* <div className="absolute top-4 left-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${item.category === 'video' ? 'bg-[#011321] text-white' : 'bg-white text-black'}`}>
                          {item.category === 'video' ? 'Video' : 'Image'}
                        </span>
                      </div> */}

                     
                      {item.category === 'image' && (
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <button
                            onClick={() =>
                              setSelectedImageIndex(galleryImages.findIndex(image => image.id === item.id))
                            }
                            className="p-3 bg-[#011321]/80 backdrop-blur-sm text-white rounded-full transform hover:scale-110 transition-all duration-300"
                          >
                            <ExternalLink className="w-6 h-6" />
                          </button>
                        </div>
                      )}

                      
                      <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            
              <div className="space-y-6">
                {filteredItems.map(item => (
                  <div key={item.id} className="group relative bg-gray-900 rounded-2xl overflow-hidden shadow-lg hover:shadow-[#011321]/40 transition-all duration-500 transform hover:-translate-y-2">
                    <div className="relative aspect-square overflow-hidden">
                      {item.category === 'image' && (
                        <img
                          src={item.url}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          onClick={() =>
                            setSelectedImageIndex(galleryImages.findIndex(image => image.id === item.id))
                          }
                        />
                      )}

                      {item.category === 'video' && (
                        <>
                          {item.url.includes('youtube') ? (
                            <iframe
                              className="rounded-2xl w-full h-full"
                              src={item.url}
                              title={item.title}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                            />
                          ) : (
                            <video className="w-full h-full object-cover" src={item.url} controls preload="metadata" />
                          )}
                        </>
                      )}

                      <div className="absolute top-4 left-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${item.category === 'video' ? 'bg-[#011321] text-white' : 'bg-white text-black'}`}>
                          {item.category === 'video' ? 'Video' : 'Image'}
                        </span>
                      </div>

                      <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                        <h3 className="text-white font-semibold text-lg">{item.title}</h3>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}


      
      {activeGalleryTab === 'awards' && <AwardsPage />}
      {activeGalleryTab === 'press' && <PressPage />}
      {activeGalleryTab === 'csr' && <CsrActivity />}

      
      {['gallery', 'image'].includes(activeGalleryTab) && (
        <ImageLightbox
          images={galleryImages}
          currentIndex={selectedImageIndex}
          isOpen={selectedImageIndex !== -1}
          onClose={() => setSelectedImageIndex(-1)}
          onNext={() => setSelectedImageIndex((prev) => (prev + 1) % galleryImages.length)}
          onPrev={() => setSelectedImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
          onSelect={(index) => setSelectedImageIndex(index)}
        />
      )}



      




    
     

      <Footer />
    </div>
  );
};

export default GalleryPage;
